import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Vector3 } from 'three';

import { SettingModelSettingType } from 'api/models';
import { useSolveConnectorPlacement } from 'api/queries/forming/forming';
import { isFramiClamp } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { raycastPlane } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { buildSolveConnectorPlacementRequest } from 'app/modules/wallforming/components/setting-container/connector-placement/utils/buildSolveConnectorPlacementRequest';
import { createPlaneFromCandidatePoints } from 'app/modules/wallforming/components/setting-container/hooks/manualSnapUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

interface ConnectorDragReference {
    position: Vector3;
    alignment: Vector3;
}

export interface ManualConnectorDragParams {
    articleId: string;
    articleNumber: string | null;
    poiIndex: number;
    connectorReference?: ConnectorDragReference;
}

export const usePrepareManualConnectorDrag = () => {
    const solveConnectorPlacementMutation = useSolveConnectorPlacement();

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(
        async ({ articleId, articleNumber, poiIndex, connectorReference }: ManualConnectorDragParams) => {
            const state = usePlacementStore.getState();

            if (!isFramiClamp(articleNumber)) {
                state.clearConnectorSnapData();
                return;
            }

            state.setConnectorSnapDataLoading(articleId, true);

            try {
                const response = await solveConnectorPlacementMutation.mutateAsync({
                    data: buildSolveConnectorPlacementRequest({
                        state,
                        sessionId: sessionguid,
                        systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                        poiIndex,
                        mode: 'Manual',
                        selectedConnectorArticleGuid: articleId
                    })
                });

                if (!response.accepted || !response.candidatePoints || response.candidatePoints.length === 0) {
                    state.clearConnectorSnapData();
                    return;
                }

                const isInsertion = !state.waitForPlacementData;
                if (!isInsertion && !connectorReference) {
                    state.clearConnectorSnapData();
                    return;
                }

                const manualInsertionPlane = createPlaneFromCandidatePoints(
                    isInsertion
                        ? response.candidatePoints
                        : connectorReference
                          ? [{ position: connectorReference.position }]
                          : [],
                    isInsertion ? state.workingPlane?.normal : connectorReference?.alignment
                );
                if (!manualInsertionPlane) {
                    state.clearConnectorSnapData();
                    return;
                }

                let anchor: Vector3;
                if (!isInsertion && connectorReference) {
                    // DRAG MODE → use ACTUAL instance position
                    anchor = connectorReference.position.clone();
                    raycastPlane.setFromNormalAndCoplanarPoint(connectorReference.alignment, anchor);
                } else {
                    // INSERT MODE → use candidate cloud
                    anchor = response.candidatePoints.reduce((currentAnchor, candidate) => {
                        currentAnchor.add(candidate.position);
                        return currentAnchor;
                    }, new Vector3());

                    anchor.multiplyScalar(1 / response.candidatePoints.length);
                    raycastPlane.setFromNormalAndCoplanarPoint(manualInsertionPlane.normal, anchor);
                }

                state.pointerPositionRef.current = anchor.clone();
                state.setConnectorSnapData(
                    articleId,
                    poiIndex,
                    response.candidatePoints,
                    manualInsertionPlane,
                    SettingModelSettingType.Connectors
                );
                usePlacementStore.setState({ waitForPlacementData: false });
            } catch {
                state.clearConnectorSnapData();
                usePlacementStore.setState({ waitForPlacementData: false });
            }
        },
        [sessionguid, solveConnectorPlacementMutation]
    );
};
